import { ChronoUnit, ZoneId } from "@js-joda/core";
import { DatasetQueryParams } from "api/model/dataset/DatasetDataInfoResponse";
import { isDatasetIdentifier, standardDeviation } from "utils/utils";
import { T } from "services/lang/LanguageService";
import { truncateDate } from "utils/time-utils";
import { convertValue, round2decimals } from "../../charts/utils";
import { IDataSet, IStatsRow } from "../models/interfaces";
import { AggregationType } from "../models/types";

export type DatasetTypes = "SECONDS" | "MINUTES" | "HOURLY" | "DAILY" | "WEEKLY" | "MONTHLY";

export const DATASET_DATA_TYPES = [
  {
    label: T("time.hourly"),
    value: "HOURLY",
  },
  {
    label: T("time.daily"),
    value: "DAILY",
  },
  {
    label: T("dataset-data-type.arbitrary"),
    value: "ARBITRARY",
  },
  {
    label: T("dataset-data-type.monthly"),
    value: "MONTHLY",
  },
  {
    label: T("dataset-data-type.weekly"),
    value: "WEEKLY",
  },
  {
    label: T("time.seconds"),
    value: "SECONDS",
  },
  {
    label: T("time.minutes"),
    value: "MINUTES",
  },
  {
    label: T("dataset-data-type.arbitrary-minutes"),
    value: "ARBITRARY_MINUTES",
  },
  {
    label: T("dataset-data-type.arbitrary-seconds"),
    value: "ARBITRARY_SECONDS",
  },
];

export const DATASET_ACCESS_TYPES = [
  {
    label: T("data-access-type.normal"),
    value: "NORMAL",
  },
  {
    label: T("data-access-type.continuous"),
    value: "CONTINUOUS",
  },
  {
    label: T("data-access-type.data-model-day"),
    value: "DATA_MODEL_DAY",
  },
];

export const removeItemById = ([...arr]: Array<any>, id: string): Array<any> => {
  const element = arr.find(a => a.identifier === id);
  const index = arr.indexOf(element);
  if (index > -1) {
    arr.splice(index, 1);
  }
  return arr;
};

const keys: any = { id: "key", title: "label", children: "children" };
export const renameNestedObject = (value: any): any => {
  if (!value || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(renameNestedObject);
  return Object.fromEntries(Object.entries(value).map(([k, v]) => [keys[k] || k, renameNestedObject(v)]));
};

export const searchTree = (element: any, matchingKey: number): any => {
  if (element.key === matchingKey) {
    return { key: element.key, label: element.label };
  }
  if (element.children != null) {
    let result = null;
    for (let i = 0; result === null && i < element.children.length; i++) {
      result = searchTree(element.children[i], matchingKey);
    }
    if (result !== null) {
      result = { key: result.key, label: result.label };
    }
    return result;
  }
  return null;
};

export const isDate2BeforeDate1 = (date1: Date | undefined, date2: Date | undefined) => {
  if (date1 !== undefined && date2 !== undefined) {
    if (date2 < date1) {
      return true;
    }
  }
};

export const getUniqueFlatArr = (arr: Array<any>) => {
  const cleanArr = arr.flat();
  return cleanArr.reduce((acc: Array<any>, current) => {
    if (!acc.includes(current) && isColorDarkEnough(current)) acc.push(current);
    return acc;
  }, []);
};

export const isColorDarkEnough = (color: string) => {
  color = color.substring(1); // strip #
  const rgb = parseInt(color, 16); // convert rrggbb to decimal
  const r = (rgb >> 16) & 0xff; // extract red
  const g = (rgb >> 8) & 0xff; // extract green
  const b = (rgb >> 0) & 0xff; // extract blue

  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

  return luma < 200;
};

export const findMin = (arr: number[]): number => {
  if (arr.length === 0) return NaN;
  let min = arr[0];
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] < min) min = arr[i];
  }
  return min;
};

export const findMax = (arr: number[]): number => {
  if (arr.length === 0) return NaN;
  let max = arr[0];
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] > max) max = arr[i];
  }
  return max;
};

export const createIStatsRows = (data: Array<IDataSet>): Array<IStatsRow> => {
  return data.map((d: IDataSet) => {
    if (d.data.length > 0) {
      const valueArr: number[] = convertValue(d.data);
      const min: number = round2decimals(findMin(valueArr));
      const max: number = round2decimals(findMax(valueArr));
      const sum: number = valueArr.reduce((a: any, b: any) => a + b, 0);
      const mean: number = sum / valueArr.length;

      return {
        title: d.title.replace(/_/g, " ").toUpperCase(),
        unit: d.unit,
        pointCount: d.data.length,
        min,
        max,
        delta: round2decimals(max - min),
        sum: round2decimals(sum),
        mean: round2decimals(mean),
        std: round2decimals(standardDeviation(valueArr, mean)!),
      };
    }
    return {
      title: d.title.replace(/_/g, " ").toUpperCase(),
      unit: d.unit,
      pointCount: d.data.length,
    };
  });
};

export const createPieData = (dArr: Array<IDataSet>, aggregationMethod: AggregationType) => {
  const plotData: { values: Array<number>; labels: Array<string> } = { values: [], labels: [] };
  dArr.forEach(d => {
    let values: number;
    const sumValues = convertValue(d.data).reduce((acc: number, curr: number) => acc + curr, 0);
    switch (aggregationMethod) {
      case "AVG":
        values = round2decimals(sumValues / d.data.length);
        break;
      case "SUM":
        values = round2decimals(sumValues);
        break;
      default:
        values = round2decimals(sumValues);
        break;
    }
    plotData.values.push(values);
    plotData.labels.push(`${d.title.replace(/_/g, " ").toUpperCase()}, ${d.unit}`);
  });
  return plotData;
};

export const calculateStepTime = (time: number, timeframe: ChronoUnit) => {
  switch (timeframe) {
    case ChronoUnit.SECONDS:
      return time * 1000;
    case ChronoUnit.MINUTES:
      return time * 60 * 1000;
    case ChronoUnit.HOURS:
      return time * 60 * 60 * 1000;
    case ChronoUnit.DAYS:
      return time * 24 * 60 * 60 * 1000;
    case ChronoUnit.MONTHS:
      return time;
    default:
      return time;
  }
};

export const formatDateBasedOnDataType = (type: DatasetTypes | null, zoneId: ZoneId) => {
  const date = new Date();
  switch (type) {
    case "SECONDS":
      return truncateDate(date, ChronoUnit.SECONDS, zoneId);
    case "MINUTES":
      return truncateDate(date, ChronoUnit.MINUTES, zoneId);
    case "HOURLY":
      return truncateDate(date, ChronoUnit.HOURS, zoneId);
    case "DAILY":
      return truncateDate(date, ChronoUnit.DAYS, zoneId);
    case "WEEKLY":
      return truncateDate(date, ChronoUnit.WEEKS, zoneId);
    case "MONTHLY":
      return truncateDate(date, ChronoUnit.MONTHS, zoneId);
    default:
      return date;
  }
};

export function mergeUniqueObjects<T extends { identifier: string }>(arr1: T[], arr2: T[]) {
  const seenIdentifiers: Set<string | null> = new Set();

  const processArray = (array: T[]) =>
    array.filter(obj => {
      const { identifier } = obj;
      return !seenIdentifiers.has(identifier) && seenIdentifiers.add(identifier);
    });

  return [...processArray(arr1), ...processArray(arr2)];
}

export const getSearchParams = (
  searchValue: string,
  lazyParams: { page: number; rows: number }
): DatasetQueryParams => {
  if (isDatasetIdentifier(searchValue))
    return {
      query: undefined,
      page: lazyParams.page,
      limit: lazyParams.rows,
      identifiers: [searchValue],
    };

  const splitValue = searchValue?.split(",");
  if (splitValue.length > 1 && splitValue.every(val => isDatasetIdentifier(val))) {
    const ids = new Set(splitValue);
    return {
      query: undefined,
      page: lazyParams.page,
      limit: lazyParams.rows,
      identifiers: [...ids],
    };
  }

  return {
    query: searchValue,
    page: lazyParams.page,
    limit: lazyParams.rows,
    identifiers: undefined,
  };
};
